import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Check, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [discountPercent, setDiscountPercent] = useState(33);
  const [billingPeriod, setBillingPeriod] = useState('yearly');
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/subscription/plans`);
        setPlan(data.plans[0] || null);
        setDiscountPercent(data.yearly_discount_percent || 33);
      } catch (error) {
        // no-op
      }
      if (user) {
        try {
          const { data } = await axios.get(`${API}/subscription/status`, { withCredentials: true });
          setSubscription(data);
        } catch {
          // not logged in
        }
      }
    };
    load();
  }, [user]);

  const handleSubscribe = async () => {
    if (!user) {
      window.location.href = '/signup';
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/subscription/checkout`,
        { tier: 'pro', origin_url: window.location.origin, billing_period: billingPeriod },
        { withCredentials: true }
      );
      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    }
    setLoading(false);
  };

  const isCurrent = subscription?.tier === 'pro';
  const priceInfo = plan && (billingPeriod === 'yearly'
    ? { primary: `$${plan.yearly_monthly_equivalent}`, secondary: '/month', subtext: `$${plan.price_yearly} billed yearly`, savings: `Save $${((plan.price * 12) - plan.price_yearly).toFixed(2)} per year` }
    : { primary: `$${plan.price}`, secondary: '/month', subtext: 'billed monthly', savings: null }
  );

  return (
    <div className="min-h-screen bg-[#F9F7F3]">
      <header className="border-b border-[#E5E0D8] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/">
            <h1 className="text-2xl font-light tracking-tight" style={{ fontFamily: 'Spectral, serif' }}>Stable Stationary</h1>
          </Link>
          <nav className="flex items-center gap-4">
            {user ? (
              <Link to="/dashboard"><Button variant="ghost" data-testid="nav-dashboard-btn">Dashboard</Button></Link>
            ) : (
              <>
                <Link to="/login"><Button variant="ghost" data-testid="nav-login-btn">Login</Button></Link>
                <Link to="/signup"><Button className="bg-[#B94A3E] hover:bg-[#9d3e33] text-white" data-testid="nav-signup-btn">Get Started</Button></Link>
              </>
            )}
          </nav>
        </div>
      </header>

      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-8">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none font-light mb-6" style={{ fontFamily: 'Spectral, serif' }}>
            One simple plan
          </h1>
          <p className="text-base leading-relaxed text-[#1A1A1A]/85 max-w-2xl mx-auto">
            Start with a 7-day free trial. No credit card required to try. Show up for your loved one, one letter at a time.
          </p>
        </div>

        {/* Billing period toggle */}
        <div className="flex justify-center mb-12" data-testid="billing-toggle">
          <div className="inline-flex items-center bg-white border border-[#E5E0D8] rounded-full p-1 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                billingPeriod === 'monthly' ? 'bg-[#1A1A1A] text-white' : 'text-[#737373] hover:text-[#1A1A1A]'
              }`}
              data-testid="billing-monthly-btn"
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
                billingPeriod === 'yearly' ? 'bg-[#1A1A1A] text-white' : 'text-[#737373] hover:text-[#1A1A1A]'
              }`}
              data-testid="billing-yearly-btn"
            >
              Yearly
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                billingPeriod === 'yearly' ? 'bg-[#B94A3E] text-white' : 'bg-[#B94A3E]/10 text-[#B94A3E]'
              }`}>
                Save {discountPercent}%
              </span>
            </button>
          </div>
        </div>

        {plan && priceInfo && (
          <div className="max-w-md mx-auto">
            <div
              className="bg-white border-2 border-[#B94A3E] rounded-md p-8 shadow-[2px_2px_0px_rgba(26,26,26,0.08)] relative"
              data-testid="plan-pro"
            >
              <div className="text-center mb-6">
                <h3 className="text-2xl mb-1" style={{ fontFamily: 'Spectral, serif' }}>{plan.name}</h3>
                <p className="text-xs label-text text-[#B94A3E] mb-3">{plan.trial_days}-day free trial</p>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-5xl font-light" style={{ fontFamily: 'Spectral, serif' }}>{priceInfo.primary}</span>
                  <span className="text-sm text-[#737373]">{priceInfo.secondary}</span>
                </div>
                <p className="text-xs text-[#737373] mt-2">{priceInfo.subtext}</p>
                {priceInfo.savings && (
                  <div className="inline-flex items-center gap-1 mt-3 bg-[#B94A3E]/10 text-[#B94A3E] px-3 py-1 rounded-full text-xs font-medium" data-testid="plan-pro-savings">
                    <Sparkles className="w-3 h-3" />
                    {priceInfo.savings}
                  </div>
                )}
              </div>
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3" data-testid={`plan-feature-${feature.slice(0, 24).replace(/\s+/g, '-').toLowerCase()}`}>
                    <Check className="w-5 h-5 text-[#B94A3E] flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={isCurrent ? () => navigate('/dashboard') : handleSubscribe}
                disabled={loading}
                className="w-full bg-[#B94A3E] hover:bg-[#9d3e33] text-white"
                data-testid="plan-pro-cta-btn"
              >
                {isCurrent ? 'Current Plan — Manage' : user ? `Start ${plan.trial_days}-Day Free Trial` : 'Sign up to start'}
              </Button>
            </div>
          </div>
        )}

        <div className="mt-16 max-w-3xl mx-auto text-center space-y-4">
          <div className="bg-white border border-[#E5E0D8] rounded-md p-6">
            <h3 className="text-xl mb-2" style={{ fontFamily: 'Spectral, serif' }}>Your trial includes:</h3>
            <p className="text-sm text-[#737373]">Full dashboard access · No credit card required to start · Cancel anytime</p>
          </div>
          <p className="text-xs text-[#737373]">Delivery timing depends on the facility's mail-processing rules and cannot be guaranteed.</p>
          <p className="text-xs text-[#737373]">
            <Link to="/compliance" className="underline hover:text-[#B94A3E]" data-testid="compliance-link">Read our compliance and trust policy</Link>
          </p>
        </div>
      </section>

      <footer className="border-t border-[#E5E0D8] bg-white py-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-sm text-[#737373]">© 2026 Stable Stationary. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
